import React from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Redirect,
  Route,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NotFound from "./pages/NotFound";
import GlobalLoading from "./components/GlobalLoading";
import { login } from "./redux/actions/userActionCreators";
import CustomRoute from "./routes/CustomRoute";
import PrivateRoute from "./routes/PrivateRoute";
import AuthRoute from "./routes/AuthRoute";
import { Suspense, useEffect, useState } from "react";
import i18n from "./i18n";
import LocaleContext from "./LocaleContext";
import Surveys from "./pages/Surveys/index";
import ProfileOrganization from "./pages/profileOrganization/ProfileOrganization";
import ProfileDetails from "./pages/profileOrganization/Profile";
import Login from "./pages/login";
import Reports from "./pages/reports";
// import ConsultReport from "./pages/reports/ConsultReport"; // retired with the consult-report backend
import Register from "./pages/register";
import ForgotPassword from "./pages/forgotPassword";
import ConfirmAccount from "./pages/register/ConfirmAccount";
import ResetPassword from "./pages/forgotPassword/ResetPassword";
// import Weaknesses from "./pages/weaknesses";
// import Home from "./pages/Home";
import RoleManagement from "./pages/roleManagement";
import { ConfirmProvider } from "material-ui-confirm";
import OrgaController from "./components/OrgaController";
// import PublicRoute from "./routes/PublicRoute"; // only consumer was the retired non_authenticated_user route
import EditOrganizationProfile from "./pages/profileOrganization/editOrganizationProfile";
import MakeUseCase from "./pages/useCase/MakeUseCase";
// import Assets from "./pages/asset";
// import UseCases from "./pages/useCase";
// import Risks from "./pages/risks";
import RiskDetails from "./pages/risks/RiskDetails";
import OrganizationList from "./pages/enterprise";
import IncompletedQuiz from "./pages/quiz/IncompletedQuiz";
import CompletedQuiz from "./pages/quiz/CompletedQuiz";
import ForwardedQuiz from "./pages/quiz/ForwardedQuiz";
import BaseQuiz from "./pages/quiz/BaseQuiz";
import MakeQuizz from "./pages/Surveys/MakeQuizz";
import ConfirmUser from "./pages/roleManagement/ConfirmUser";
import ForwardedAlreadyAnswered from "./pages/quiz/ForwardedAlreadyAnswered";
import OrganizationAssets from "./pages/organizationAssets";
import ConfirmInvitationModal from "./components/ConfirmInvitationModal";
import AutomaticLogOutControl from "./components/AutomaticLogOutControl";
import TabManagement from "./pages/tabManagement";
import { refreshAccessToken } from "./axios/index";
import PowerBi from "./pages/powerBi";
// import TefDetails from "./pages/powerBi/dashboards/info/tefDetails";
import EditOrganizationHandler from "./components/Profile/EditOrganizationHandler";
import ExportHandler from "./pages/powerBi/dashboards/exportHandler";
import Workstation from "./pages/workstation";
import Evaluation from "./pages/evaluation";
import ThreatLandscape from "./pages/threatLandscape";
import StaticPage from "./pages/static-page";
import DiscoveredAssets from "./pages/organizationAssets/Discovered_Assets";
import RiskScenarios from "./pages/riskScenarios";
import RiskAssessment from "./pages/riskAssessment";
import { clearRiskAssessmentStorage } from "./utils/storageCleanup";

// setItem("orgaId", null) stores the string "null", which is truthy, so reading
// the key raw puts "null" in the store as an organisation id (F-63). Same idiom
// the refresh token used before it stopped reaching the browser: a value that
// stringified an absence is an absence.
const storedOrgaId = () => {
  const stored = localStorage.getItem("orgaId");
  if (!stored || stored === "null" || stored === "undefined") {
    return null;
  }
  return stored;
};

function App() {
  const dispatch = useDispatch();
  // The two /profile-organization language routes below branch on being signed
  // in. That used to be a localStorage token read; isAuth is what the three route
  // guards already use, and unlike a module variable it re-renders when it moves.
  const { isAuth } = useSelector((state) => state.user);
  const storedUser = localStorage.getItem("user");

  let parsedStoredUser = null;
  if (storedUser) {
    try {
      parsedStoredUser = JSON.parse(storedUser);
    } catch (e) {
      parsedStoredUser = null;
    }
  }
  const enterpriseDefaultPath =
    parsedStoredUser?.account_type === "enterprise"
      ? parsedStoredUser?.role === "cytailor_admin"
        ? "/super_admin_management"
        : "/enterprise"
      : "/quizzes";

  useEffect(() => {
    clearRiskAssessmentStorage();
  }, []);

  // Three states, not two. A reload starts with no access token - it lives in
  // memory now - so until the refresh cookie has been exchanged we do not know
  // whether this is a live session or an anonymous visitor. Rendering the router
  // before the answer arrives is what flashes the login page at a logged-in user,
  // because PrivateRoute redirects on !isAuth.
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    // Nothing to restore, so nothing to ask for: the landing page is public and an
    // anonymous visitor must not spend a request on /auth/refresh. The cookie is
    // HttpOnly and invisible to JS, so this key is the only hint available - it is
    // written at login and cleared at logout, and it is display data rather than a
    // credential, which is why it may be read for this. Worst case is a browser
    // whose localStorage was cleared but whose cookie lives: it looks signed out
    // and signs in again.
    if (!localStorage.getItem("user")) {
      setSessionChecked(true);
      return;
    }

    let active = true;

    // Shared with the response interceptor's single-flight promise, so a boot
    // refresh and a 401-triggered one can never become two rotations.
    refreshAccessToken()
      .then((token) => {
        if (!active || !token) {
          return;
        }
        // organization_id comes from the value login itself stored. redirectUrl is
        // a login-flow concept and there is none at boot - AuthRoute derives its
        // own from the URL. Dispatching the token alone is what left both
        // undefined in the store (F-25).
        dispatch(login(token, storedOrgaId()));
      })
      .catch(() => {
        // No cookie, or keycloak refused it: an anonymous visitor. There is
        // nothing to clear and nowhere to redirect - the route guards do that.
      })
      .finally(() => {
        if (active) {
          setSessionChecked(true);
        }
      });

    return () => {
      active = false;
    };
  }, [dispatch]);

  const [locale, setLocale] = useState(
    i18n?.language?.indexOf("fr") !== -1 ? "fr" : "en"
  );

  useEffect(() => {
    // Registered once, not on every render: each fired listener called setLocale,
    // which rendered, which registered another one (F-27).
    const onLanguageChanged = () =>
      setLocale(i18n?.language?.indexOf("fr") !== -1 ? "fr" : "en");

    i18n.on("languageChanged", onLanguageChanged);
    return () => i18n.off("languageChanged", onLanguageChanged);
  }, []);

  if (!sessionChecked) {
    return <GlobalLoading locale={locale} />;
  }

  return (
    <>
      <ConfirmProvider>
        <GlobalLoading locale={locale} />

        <LocaleContext.Provider value={{ locale, setLocale }}>
          <Suspense fallback={<GlobalLoading locale={locale} />}>
            <Router>
              <ConfirmInvitationModal />
              <OrgaController />
              <AutomaticLogOutControl />
              <Switch>
                <Route exact path="/" component={StaticPage} />
                <Route
                  exact
                  path="/login/fr"
                  render={() => {
                    i18n.changeLanguage("fr");
                    return (
                      <Redirect
                        to={{
                          pathname: "/login",
                        }}
                      />
                    );
                  }}
                />
                <Route
                  exact
                  path="/login/en"
                  render={() => {
                    i18n.changeLanguage("en");
                    return (
                      <Redirect
                        to={{
                          pathname: "/login",
                        }}
                      />
                    );
                  }}
                />

                {/*chat bot code*/}

                {/*<Route*/}
                {/*    path="/chat"*/}
                {/*    render={() => {*/}
                {/*        const token = localStorage.getItem("token");*/}
                {/*        const userRole = JSON.parse(localStorage.getItem("user"))?.role;*/}

                {/*        // Check if the user is authenticated*/}
                {/*        if (!token) {*/}
                {/*            return (*/}
                {/*                <Redirect*/}
                {/*                    to={{*/}
                {/*                        pathname: "/login",*/}
                {/*                    }}*/}
                {/*                />*/}
                {/*            );*/}
                {/*        }*/}

                {/*        // Check if the user has the "enterprise_user" role*/}
                {/*        if (userRole !== "enterprise_user") {*/}
                {/*            return (*/}
                {/*                <Redirect*/}
                {/*                    to={{*/}
                {/*                        pathname: "/enterprise",*/}
                {/*                    }}*/}
                {/*                />*/}
                {/*            );*/}
                {/*        }*/}

                {/*        // If authenticated and role is correct, render the ChatBot component*/}
                {/*        return <PrivateRoute path="/chat" component={ChatBot} />*/}
                {/*    }}*/}
                {/*/>*/}

	                <Route
	                  path="/profile-organization/fr"
	                  render={() => {
	                    i18n.changeLanguage("fr");
	                    if (isAuth) {
	                      if (parsedStoredUser?.account_type === "enterprise") {
	                        return (
	                          <Redirect
	                            to={{
	                              pathname: enterpriseDefaultPath,
	                            }}
	                          />
	                        );
	                      } else {
                        return (
                          <Redirect
                            to={{
                              pathname: "/quizzes",
                            }}
                          />
                        );
                      }
                    } else {
                      return (
                        <Redirect
                          to={{
                            pathname: "/profile-organization",
                          }}
                        />
                      );
                    }
                  }}
                />
	                <Route
	                  path="/profile-organization/en"
	                  render={() => {
	                    i18n.changeLanguage("en");
	                    if (isAuth) {
	                      if (parsedStoredUser?.account_type === "enterprise") {
	                        return (
	                          <Redirect
	                            to={{
	                              pathname: enterpriseDefaultPath,
	                            }}
	                          />
	                        );
	                      } else {
                        return (
                          <Redirect
                            to={{
                              pathname: "/quizzes",
                            }}
                          />
                        );
                      }
                    } else {
                      return (
                        <Redirect
                          to={{
                            pathname: "/profile-organization",
                          }}
                        />
                      );
                    }
                  }}
                />

                <CustomRoute
                  exact
                  path="/profile-organization"
                  component={ProfileOrganization}
                />

                {/*<CustomRoute*/}
                {/*  exact*/}
                {/*  path="/quizzes"*/}
                {/*  component={Surveys}*/}
                {/*/>*/}

                {/* <CustomRoute
                  exact
                  path="/quizzes/:category?"
                  component={Surveys}
                /> */}
                <CustomRoute
                  path="/base_quizzes/:quizId"
                  component={BaseQuiz}
                />

                <PrivateRoute
                  path="/asset_inventory"
                  component={DiscoveredAssets}
                />
                {/* <PrivateRoute
                  path="/asset_inventory"
                  component={ThreatLandscape}
                /> */}
                <PrivateRoute path="/evaluation" component={Evaluation} />
                {/* <PrivateRoute path="/workstation" component={Workstation} /> */}
                <PrivateRoute path="/profile" component={ProfileDetails} />
                <PrivateRoute
                  path="/risk-scenarios"
                  component={RiskScenarios}
                />
                <PrivateRoute
                  path="/risk-assessment/:orgaId?"
                  component={RiskAssessment}
                />
                <PrivateRoute exact path="/reports" component={Reports} />
                <PrivateRoute
                  path="/make-usecase/:caseId"
                  component={MakeUseCase}
                />
                <PrivateRoute
                  path="/orga_assets"
                  component={OrganizationAssets}
                />
                {/* <PrivateRoute path="/assets" component={Assets} /> */}
                <PrivateRoute path="/enterprise" component={OrganizationList} />
                <PrivateRoute
                  path="/super_admin_management"
                  allowedRoles={["cytailor_admin"]}
                  component={TabManagement}
                />
                <PrivateRoute
                  exact
                  path="/risk_profile/:page?"
                  component={PowerBi}
                />
                <PrivateRoute
                  exact
                  path="/export/risk_profile"
                  component={ExportHandler}
                />
                {/* <PrivateRoute path="/risk_profile/tef" component={TefDetails} /> */}

                {/* <PrivateRoute exact path="/weakness" component={Weaknesses} /> */}
                {/* <PrivateRoute exact path="/risk" component={Risks} /> */}
                <PrivateRoute path="/risk/:riskId" component={RiskDetails} />
                <PrivateRoute exact path="/users" component={RoleManagement} />
                <AuthRoute path="/login" component={Login} />
                <AuthRoute path="/register" component={Register} />
                <AuthRoute
                  path="/confirm-email/:idAccount"
                  component={ConfirmAccount}
                />
                <AuthRoute path="/forgot-password" component={ForgotPassword} />
                {/* <PrivateRoute exact path="/usecases" component={UseCases} /> */}
                <PrivateRoute
                  exact
                  path="/Already-Answered"
                  component={ForwardedAlreadyAnswered}
                />
                <PrivateRoute
                  exact
                  path="/incompleted_quizzes/:quizId"
                  component={IncompletedQuiz}
                />
                <PrivateRoute
                  exact
                  path="/completed_quizzes/:quizId"
                  component={CompletedQuiz}
                />
                <PrivateRoute
                  path="/make-quizz/:quizzId"
                  component={MakeQuizz}
                />
                <PrivateRoute
                  path="/edit-profile-organization"
                  component={EditOrganizationHandler}
                />
                <PrivateRoute
                  exact
                  path="/forwarded_usecase/:forwardId"
                  component={ForwardedQuiz}
                />

                <AuthRoute
                  path="/reset-password/:token"
                  component={ResetPassword}
                />

                <AuthRoute
                  path="/confirm-user/:token"
                  component={ConfirmUser}
                />

                {/* Retired with the offline/consult report backend. Its API
                    (/calculator/consult_report, /calculator_off) no longer
                    exists; reports are served from /survey/reports. */}
                {/* <CustomRoute
                  exact
                  path="/consult-report/:reportId"
                  component={ConsultReport}
                /> */}

                {/* <PrivateRoute
                  exact
                  path="/reports/:reportId"
                  component={Reports}
                /> */}

                {/* Retired: nothing generated this link - no caller anywhere in
                    the frontend or the services. /consult-report stays, it is
                    the shared report link built in reports/statusCard. */}
                {/* <PublicRoute
                  exact
                  path="/reports/non_authenticated_user/:reportId"
                  component={Reports}
                /> */}
                <CustomRoute component={NotFound} />
              </Switch>
            </Router>
          </Suspense>
        </LocaleContext.Provider>
      </ConfirmProvider>
    </>
  );
}

export default App;
